import { resetAuthExpiryHandling } from '@glyphic/shared-core'
import type { UserAuthLoginData } from '@glyphic/user-api-types'
import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react'
import { useNavigate } from 'react-router-dom'

import { ensureAuthInterceptors, registerAuthSessionBridge } from '../api/authInterceptors'
import {
  clearSession,
  getAuthToken,
  markLoggedIn as markLoggedInStorage,
  setAuthToken,
} from '../lib/session'
import {
  clearUserAuthorization,
  writeUserAuthorization,
} from '../lib/userAuthorization'
import { readStoredUserPublicId, writeUserPublicId } from '../lib/userPublicId'
import { useToast } from './ToastContext'

type AuthSessionValue = {
  token: string | null
  userPublicId: string | null
  isLoggedIn: boolean
  hydrateFromStorage: () => void
  markLoggedIn: (data: UserAuthLoginData) => void
  clearState: () => void
}

const AuthSessionContext = createContext<AuthSessionValue | null>(null)

export function AuthSessionProvider({ children }: { children: ReactNode }) {
  const navigate = useNavigate()
  const { showToast } = useToast()
  const [token, setToken] = useState<string | null>(() => getAuthToken())
  const [userPublicId, setUserPublicId] = useState<string | null>(() =>
    readStoredUserPublicId(),
  )

  const hydrateFromStorage = useCallback(() => {
    setToken(getAuthToken())
    setUserPublicId(readStoredUserPublicId())
  }, [])

  const clearState = useCallback(() => {
    clearSession()
    clearUserAuthorization()
    try {
      localStorage.removeItem('glyphicai-user-public-id')
    } catch {
      /* ignore */
    }
    setToken(null)
    setUserPublicId(null)
  }, [])

  const markLoggedIn = useCallback((data: UserAuthLoginData) => {
    setAuthToken(data.token)
    writeUserPublicId(data.userPublicId)
    markLoggedInStorage()
    writeUserAuthorization({ roles: data.roles, permissions: data.permissions })
    resetAuthExpiryHandling()
    setToken(data.token.trim())
    setUserPublicId(data.userPublicId.trim())
  }, [])

  useEffect(() => {
    ensureAuthInterceptors()
    registerAuthSessionBridge({
      showToast,
      navigate,
      clearState,
    })
  }, [showToast, navigate, clearState])

  const isLoggedIn = Boolean(token)

  const value = useMemo<AuthSessionValue>(
    () => ({
      token,
      userPublicId,
      isLoggedIn,
      hydrateFromStorage,
      markLoggedIn,
      clearState,
    }),
    [token, userPublicId, isLoggedIn, hydrateFromStorage, markLoggedIn, clearState],
  )

  return (
    <AuthSessionContext.Provider value={value}>{children}</AuthSessionContext.Provider>
  )
}

export function useAuthSession(): AuthSessionValue {
  const ctx = useContext(AuthSessionContext)
  if (!ctx) {
    throw new Error('useAuthSession 须在 AuthSessionProvider 内使用')
  }
  return ctx
}
